import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { FaPlus, FaEdit, FaTrash } from "react-icons/fa";
import "./css/paysPopup.css";
import { BACKEND_URL } from "../constants";
import { useAuth } from "../navigation/AuthProvider";

export const PaysPopup = ({ onClose, onUpdatePays, setLoading, readOnly}) => {
  const { company, user, pays, setPays } = useAuth();
  const [allPays, setAllPays] = useState(pays || []);
  const [editingPay, setEditingPay] = useState(null);
  const [showForm, setShowForm] = useState(false);

  // Form: name, amount, payType (hourly | monthly), monthlyHoursNorm (only for monthly)
  const [form, setForm] = useState({
    name: "",
    amount: "",
    payType: "hourly",
    monthlyHoursNorm: "",
  });

  // Filter: all | hourly | monthly
  const [typeFilter, setTypeFilter] = useState("all");

  // Sök
  const [searchTerm, setSearchTerm] = useState("");

  // --- Scroll-logic (samma som AbsencePopup) ---
  const firstItemRef = useRef(null);
  const listRef = useRef(null);
  const [rowHeight, setRowHeight] = useState(null);

  useEffect(() => {
    if (firstItemRef.current) {
      const h = firstItemRef.current.getBoundingClientRect().height;
      if (h && h > 0) setRowHeight(Math.ceil(h));
    }
  }, [allPays, showForm]);

  const shouldScroll = allPays.length > 5;
  const computedMaxHeight =
    shouldScroll && rowHeight ? `${rowHeight * 5.5}px` : "none";
  // ------------------------------------------------

  const resetForm = () =>
    setForm({
      name: "",
      amount: "",
      payType: "hourly",
      monthlyHoursNorm: "",
    });

  const handleSave = async () => {
    if (!form.name.trim() || form.amount === "" || isNaN(Number(form.amount))) {
      alert("Namn och giltigt belopp krävs.");
      return;
    }

    if (form.payType !== "hourly" && form.payType !== "monthly") {
      alert("Välj lönetyp (timlön eller månadslön).");
      return;
    }

    // ✅ NEW: validate norm hours only for monthly pay
    if (form.payType === "monthly") {
      const normNum = Number(form.monthlyHoursNorm);
      if (form.monthlyHoursNorm === "" || isNaN(normNum) || normNum <= 0) {
        alert("Ange giltiga normtimmar för månadslön (t.ex. 160).");
        return;
      }
    }

    const endpoint = editingPay ? "update-pay" : "add-new-pay";
    const method = editingPay ? axios.put : axios.post;

    // ✅ NEW: only send monthlyHoursNorm for monthly pays
    const monthlyHoursNormPayload =
      form.payType === "monthly"
        ? { monthlyHoursNorm: Number(form.monthlyHoursNorm) }
        : { monthlyHoursNorm: null };

    const payload = editingPay
      ? {
          userId: user._id,
          companyId: company._id,
          payId: editingPay._id,
          updates: {
            name: form.name,
            amount: form.amount,
            payType: form.payType,
            ...monthlyHoursNormPayload,
          },
        }
      : {
          userId: user._id,
          companyId: company._id,
          newPay: {
            name: form.name,
            amount: form.amount,
            payType: form.payType,
            ...monthlyHoursNormPayload,
          },
        };

    try {
      const { data } = await method(`${BACKEND_URL}/${endpoint}`, payload, {
        withCredentials: true,
      });

      if (data.status === "success") {
        setAllPays(data.pays);
        setPays(data.pays);
        onUpdatePays({ updatedPays: data.pays });
        setEditingPay(null);
        setShowForm(false);
        resetForm();
      } else {
        alert(data.errorText);
      }
    } catch {
      alert("Fel vid sparning av lön.");
    }
  };

  const handleDelete = async (payId) => {
    if (!window.confirm("Är du säker på att du vill radera lönen?")) return;
    try {
      const { data } = await axios.delete(`${BACKEND_URL}/delete-pay`, {
        data: {
          userId: user._id,
          companyId: company._id,
          payId,
        },
        withCredentials: true,
      });

      if (data.status === "success") {
        alert(
          "Alla användare som tidigare var registrerade till lönen har nu ingen lönetyp."
        );
        setAllPays(data.pays);
        setPays(data.pays);
        onUpdatePays({ updatedPays: data.pays });
      }
    } catch {
      alert("Kunde inte radera lön");
    }
  };

  const openForm = (pay = null) => {
    if (pay) {
      setEditingPay(pay);
      setForm({
        name: pay.name || "",
        amount: pay.amount ?? "",
        // gamla poster utan payType behandlas som timlön
        payType: pay.payType === "monthly" ? "monthly" : "hourly",
        monthlyHoursNorm:
          pay.payType === "monthly" && pay.monthlyHoursNorm != null
            ? String(pay.monthlyHoursNorm)
            : "",
      });
    } else {
      setEditingPay(null);
      resetForm();
    }
    setShowForm(true);
  };

  const amountPlaceholder =
    form.payType === "monthly" ? "Ex. 28000" : "Ex. 140";

  const unitLabelForList = (pay) =>
    pay.payType === "monthly" ? "kr/mån" : "kr/h";

  // Filter + sök (ingen annan sort)
  const filteredPays = allPays
    .filter((p) => {
      const type = p.payType === "monthly" ? "monthly" : "hourly";
      if (typeFilter === "all") return true;
      if (typeFilter === "hourly") return type === "hourly";
      if (typeFilter === "monthly") return type === "monthly";
      return true;
    })
    .filter((p) => {
      if (!searchTerm.trim()) return true;
      const name = (p.name || "").toLowerCase();
      return name.includes(searchTerm.toLowerCase());
    });

  return (
    <div className="pays-popup">
      <div className="pays-popup__overlay" onClick={onClose} />
      <div className="pays-popup__content">
        <h2 className="pays-popup__title">Lönetyp Hantering</h2>
        <p className="pays-popup__description">
          Skapa och hantera olika löner (timlön &amp; månadslön) för företaget.
        </p>

        {/* Sök + filter (alla/tim/månad) på samma rad */}
        <div className="pays-popup__toolbar">
          <div className="pays-popup__toolbar-item">
            <input
              type="text"
              placeholder="Sök lönetyp..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="pays-popup__toolbar-item">
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <option value="all">Alla lönetyper</option>
              <option value="hourly">Endast timlöner</option>
              <option value="monthly">Endast månadslöner</option>
            </select>
          </div>
        </div>

        <div
          className="pays-popup__list"
          role="list"
          ref={listRef}
          style={{
            position: "relative",
            maxHeight: computedMaxHeight,
            minHeight: computedMaxHeight,
            overflowY: shouldScroll ? "auto" : "visible",
            paddingRight: 8,
            scrollBehavior: "smooth",
            boxShadow: shouldScroll
              ? "inset 0 -1px 0 rgba(0,0,0,0.06)"
              : "none",
            borderRadius: 8,
          }}
        >
          {filteredPays.map((pay, index) => (
            <div
              key={pay._id}
              className="pays-popup__item"
              ref={index === 0 ? firstItemRef : null}
              role="listitem"
              style={{
                minHeight: rowHeight ? `${rowHeight}px` : undefined,
                boxSizing: "border-box",
              }}
            >
              <div>
                <strong>{pay.name}</strong> – {pay.amount}{" "}
                {unitLabelForList(pay)}
                {/* ✅ NEW: show norm hours inline for monthly */}
                {pay.payType === "monthly" && pay.monthlyHoursNorm != null && (
                  <span style={{ color: "#666", fontSize: 12, marginLeft: 6 }}>
                    (norm {Number(pay.monthlyHoursNorm).toFixed(1)}h/mån)
                  </span>
                )}
              </div>
              <div className="pays-popup__actions">
                {!readOnly && (
                  <>
                  <FaEdit onClick={() => openForm(pay)} title="Redigera" />
                  <FaTrash
                    onClick={() => handleDelete(pay._id)}
                    title="Ta bort"
                  />
                  </>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="pays-popup__footer">
          {!readOnly && (
              <button
                className="pays-popup__add-btn"
                onClick={() => openForm()}
              >
                <FaPlus /> Skapa ny lön
              </button>
            )}
        </div>

        {showForm && (
          <div className="pays-form">
            <div
              className="pays-form__overlay"
              onClick={() => setShowForm(false)}
            />
            <div className="pays-form__content">
              <h3>{editingPay ? "Redigera lön" : "Ny lönetyp"}</h3>

              <div className="pays-form__field">
                <label>Namn</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) =>
                    setForm({ ...form, name: e.target.value })
                  }
                  placeholder="Ex. Daglön"
                />
              </div>

              <div className="pays-form__field">
                <label>Lönetyp</label>
                <div className="pays-form__type-options">
                  <label className="pays-form__type-option">
                    <input
                      type="radio"
                      value="hourly"
                      checked={form.payType === "hourly"}
                      onChange={(e) =>
                        setForm({ ...form, payType: e.target.value, monthlyHoursNorm: "" })
                      }
                    />
                    <span style={{marginLeft: 4}}>Timlön (kr/timme)</span>
                  </label>

                  <label className="pays-form__type-option">
                    <input
                      type="radio"
                      value="monthly"
                      checked={form.payType === "monthly"}
                      onChange={(e) =>
                        setForm({ ...form, payType: e.target.value })
                      }
                    />
                    <span style={{marginLeft: 4}}>Månadslön (kr/månad)</span>
                  </label>
                </div>
              </div>

              <div className="pays-form__field">
                <label>Belopp</label>
                <input
                  type="number"
                  value={form.amount}
                  onChange={(e) =>
                    setForm({ ...form, amount: e.target.value })
                  }
                  placeholder={amountPlaceholder}
                />
              </div>

              {/* ✅ NEW: Norm hours only for monthly */}
              {form.payType === "monthly" && (
                <div className="pays-form__field">
                  <label>Standardtimmar per månad</label>
                  <input
                    type="number"
                    value={form.monthlyHoursNorm}
                    onChange={(e) =>
                      setForm({ ...form, monthlyHoursNorm: e.target.value })
                    }
                    placeholder="Ex. 160"
                  />
                </div>
              )}

              <div className="pays-form__actions">
                <button className="save" onClick={handleSave}>
                  Spara
                </button>
                <button
                  className="cancel"
                  onClick={() => setShowForm(false)}
                >
                  Avbryt
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaysPopup;
